import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import {
  BellIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  UserIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';

const NotificationManagement = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  
  // 상태 관리
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    today: 0,
    types: {}
  });
  
  // 필터 상태
  const [filters, setFilters] = useState({
    searchType: 'user', // 'user', 'contractor', 'content'
    searchQuery: '',
    dateFrom: '',
    dateTo: '',
    notificationType: 'all',
    readStatus: 'all', // 'all', 'read', 'unread'
    projectId: '',
    projectStatus: 'all' // 프로젝트 상태
  });
  
  // 검색 타입 옵션
  const searchTypes = [
    { value: 'user', label: '사용자 (이름/ID/전화번호)' },
    { value: 'contractor', label: '소장 (이름/ID)' },
    { value: 'content', label: '알림 내용' },
    { value: 'project', label: '프로젝트 (ID/제목)' }
  ];
  
  // 알림 타입 옵션
  const notificationTypes = [
    { value: 'all', label: '전체' },
    { value: 'message', label: '메시지' },
    { value: 'message_received', label: '메시지 수신' },
    { value: 'new_chat_message', label: '새 채팅' },
    { value: 'project_status', label: '프로젝트 상태' },
    { value: 'project_assigned', label: '프로젝트 배정' },
    { value: 'project_created', label: '프로젝트 생성' },
    { value: 'reward', label: '리워드' },
    { value: 'notice', label: '공지' }
  ];
  
  // 프로젝트 상태 옵션
  const projectStatuses = [
    { value: 'all', label: '전체' },
    { value: 'received', label: '접수' },
    { value: 'review', label: '검토' },
    { value: 'contract', label: '계약' },
    { value: 'progress', label: '진행' },
    { value: 'completed', label: '완료' },
    { value: 'cancelled', label: '취소' }
  ];
  
  // 알림 아이콘 매핑
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
      case 'message_received':
      case 'new_chat_message':
        return <ChatBubbleLeftRightIcon className="h-4 w-4" />;
      case 'project_status':
      case 'project_assigned':
      case 'project_created':
        return <ClipboardDocumentCheckIcon className="h-4 w-4" />;
      case 'reward':
        return <CurrencyDollarIcon className="h-4 w-4" />;
      case 'notice':
        return <MegaphoneIcon className="h-4 w-4" />;
      default:
        return <BellIcon className="h-4 w-4" />;
    }
  };
  
  // 알림 타입 색상 매핑
  const getNotificationColor = (type) => {
    switch (type) {
      case 'message':
      case 'message_received':
      case 'new_chat_message':
        return 'bg-blue-50 text-blue-700';
      case 'project_status':
      case 'project_assigned':
      case 'project_created':
        return 'bg-green-50 text-green-700';
      case 'reward':
        return 'bg-yellow-50 text-yellow-700';
      case 'notice':
        return 'bg-purple-50 text-purple-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };
  
  // 통계 조회
  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/notifications/stats');
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch notification stats:', error);
    }
  };

  // 알림 목록 조회
  const fetchNotifications = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: pageSize,
        ...filters
      });
      
      const response = await api.get(`/admin/notifications?${params}`);
      setNotifications(response.data.notifications || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
      setCurrentPage(page);
      
      // 통계는 별도로 조회
      fetchStats();
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // 검색 처리
  const handleSearch = () => {
    fetchNotifications(1);
  };
  
  // 필터 초기화
  const handleResetFilters = () => {
    setFilters({
      searchType: 'user',
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
      notificationType: 'all',
      readStatus: 'all',
      projectId: '',
      projectStatus: 'all'
    });
    fetchNotifications(1);
  };
  
  // 선택 토글
  const toggleSelection = (id) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };
  
  // 전체 선택
  const toggleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    }
  };
  
  // 선택한 알림 삭제
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    
    if (!window.confirm(`선택한 ${selectedIds.size}개의 알림을 삭제하시겠습니까?`)) {
      return;
    }
    
    try {
      await api.delete('/admin/notifications', {
        data: { notificationIds: Array.from(selectedIds) }
      });
      setSelectedIds(new Set());
      fetchNotifications(currentPage);
    } catch (error) {
      console.error('Failed to delete notifications:', error);
      alert('알림 삭제에 실패했습니다.');
    }
  };

  // 검색 결과 전체 삭제
  const handleDeleteFiltered = async () => {
    const isFiltered = filters.searchQuery || 
                      filters.notificationType !== 'all' || 
                      filters.readStatus !== 'all' ||
                      filters.dateFrom || 
                      filters.dateTo ||
                      filters.projectId ||
                      filters.projectStatus !== 'all';
    
    if (!isFiltered) {
      alert('검색 조건을 설정해주세요.');
      return;
    }
    
    const confirmMessage = `현재 검색 조건에 해당하는 ${totalCount}개의 알림을 모두 삭제하시겠습니까?\n\n` +
                          `검색 조건:\n` +
                          (filters.searchQuery ? `- 검색어: ${filters.searchQuery}\n` : '') +
                          (filters.notificationType !== 'all' ? `- 알림 타입: ${filters.notificationType}\n` : '') +
                          (filters.readStatus !== 'all' ? `- 읽음 상태: ${filters.readStatus === 'read' ? '읽음' : '읽지 않음'}\n` : '') +
                          (filters.dateFrom ? `- 시작 날짜: ${filters.dateFrom}\n` : '') +
                          (filters.dateTo ? `- 종료 날짜: ${filters.dateTo}\n` : '') +
                          (filters.projectId ? `- 프로젝트 ID: ${filters.projectId}\n` : '') +
                          (filters.projectStatus !== 'all' ? `- 프로젝트 상태: ${filters.projectStatus}\n` : '');
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const result = await api.delete('/admin/notifications/by-filter', {
        data: filters
      });
      
      alert(`${result.data.deletedCount}개의 알림이 삭제되었습니다.`);
      setSelectedIds(new Set());
      fetchNotifications(1);
      fetchStats();
    } catch (error) {
      console.error('Failed to delete filtered notifications:', error);
      alert('검색 결과 삭제에 실패했습니다.');
    }
  };
  
  // 엑셀 내보내기
  const handleExport = async () => {
    try {
      const params = new URLSearchParams(filters);
      const response = await api.get(`/admin/notifications/export?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notifications_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export notifications:', error);
      alert('내보내기에 실패했습니다.');
    }
  };
  
  // 날짜 포맷팅
  const formatDate = (date) => {
    if (!date) return '-';
    return format(new Date(date), 'yyyy-MM-dd HH:mm', { locale: ko });
  };
  
  // 컴포넌트 마운트 시 초기 데이터 로드
  useEffect(() => {
    fetchNotifications(1);
    fetchStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 엔터키로 검색
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-white shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <h1 className="text-2xl font-bold text-gray-900">알림 내역 관리</h1>
          </div>
        </div>
      </div>
      
      {/* 통계 카드 */}
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">전체 알림</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total.toLocaleString()}</p>
              </div>
              <BellIcon className="h-8 w-8 text-gray-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">읽지 않은 알림</p>
                <p className="text-2xl font-bold text-red-600">{stats.unread.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.total > 0 ? `${((stats.unread / stats.total) * 100).toFixed(1)}%` : '0%'}
                </p>
              </div>
              <ExclamationCircleIcon className="h-8 w-8 text-red-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">오늘 발송</p>
                <p className="text-2xl font-bold text-blue-600">{stats.today.toLocaleString()}</p>
              </div>
              <CalendarIcon className="h-8 w-8 text-blue-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">현재 페이지</p>
                <p className="text-2xl font-bold text-green-600">
                  {notifications.length}
                  <span className="text-sm text-gray-500 ml-1">/ {totalCount}</span>
                </p>
              </div>
              <CheckCircleIcon className="h-8 w-8 text-green-400" />
            </div>
          </div>
        </div>
      </div>
      
      {/* 필터 및 검색 */}
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* 검색 타입 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                검색 유형
              </label>
              <select
                value={filters.searchType}
                onChange={(e) => setFilters({ ...filters, searchType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {searchTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            {/* 검색어 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                검색어
              </label>
              <input
                type="text"
                value={filters.searchQuery}
                onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                onKeyPress={handleKeyPress}
                placeholder="검색어 입력"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* 알림 타입 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                알림 타입
              </label>
              <select
                value={filters.notificationType}
                onChange={(e) => setFilters({ ...filters, notificationType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {notificationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            {/* 읽음 상태 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                읽음 상태
              </label>
              <select
                value={filters.readStatus}
                onChange={(e) => setFilters({ ...filters, readStatus: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">전체</option>
                <option value="unread">읽지 않음</option>
                <option value="read">읽음</option>
              </select>
            </div>
            
            {/* 날짜 범위 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작 날짜
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                종료 날짜
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* 프로젝트 ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프로젝트 ID
              </label>
              <input
                type="text"
                value={filters.projectId}
                onChange={(e) => setFilters({ ...filters, projectId: e.target.value })}
                placeholder="프로젝트 ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* 프로젝트 상태 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                프로젝트 상태
              </label>
              <select
                value={filters.projectStatus}
                onChange={(e) => setFilters({ ...filters, projectStatus: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {projectStatuses.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* 검색 버튼 */}
          <div className="mt-4 flex justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <MagnifyingGlassIcon className="h-5 w-5 inline mr-2" />
                검색
              </button>
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                초기화
              </button>
              {/* 검색 결과 건수 표시 */}
              {(filters.searchQuery || filters.notificationType !== 'all' || filters.readStatus !== 'all' || 
                filters.dateFrom || filters.dateTo || filters.projectId || filters.projectStatus !== 'all') && (
                <div className="ml-4 text-sm text-gray-600">
                  검색 결과: <span className="font-bold text-indigo-600">{totalCount}건</span>
                </div>
              )}
            </div>
            
            <div className="flex space-x-2">
              {/* 검색 결과 전체 삭제 버튼 */}
              {(filters.searchQuery || filters.notificationType !== 'all' || filters.readStatus !== 'all' || 
                filters.dateFrom || filters.dateTo || filters.projectId || filters.projectStatus !== 'all') && totalCount > 0 && (
                <button
                  onClick={handleDeleteFiltered}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <TrashIcon className="h-5 w-5 inline mr-2" />
                  검색 결과 전체 삭제 ({totalCount})
                </button>
              )}
              {selectedIds.size > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <TrashIcon className="h-5 w-5 inline mr-2" />
                  선택 삭제 ({selectedIds.size})
                </button>
              )}
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <ArrowDownTrayIcon className="h-5 w-5 inline mr-2" />
                내보내기
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 알림 목록 테이블 */}
      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === notifications.length && notifications.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수신자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  타입
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  내용
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  발송일시
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  프로젝트
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-20 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                    </div>
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-20 text-center text-gray-500">
                    알림이 없습니다.
                  </td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(notification.id)}
                        onChange={() => toggleSelection(notification.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {notification.user_name || '-'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {notification.user_email || notification.user_phone || '-'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationColor(notification.type)}`}>
                        {getNotificationIcon(notification.type)}
                        <span className="ml-1">{notification.type}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {notification.title}
                      </div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {notification.content}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {notification.is_read ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          읽음
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          읽지 않음
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(notification.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {notification.project_id ? (
                        <button
                          onClick={() => navigate(`/projects/${notification.project_id}`)}
                          className="text-indigo-600 hover:text-indigo-900 text-sm"
                        >
                          #{notification.project_id}
                        </button>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col items-center space-y-4">
            {/* 페이지 정보 */}
            <div className="text-sm text-gray-600">
              전체 <span className="font-bold">{totalCount}건</span> 중 
              <span className="font-bold"> {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)}번째</span> 표시
              (페이지 {currentPage}/{totalPages})
            </div>
            
            {/* 페이지 버튼 */}
            <div className="flex items-center space-x-2">
              {/* 처음 페이지로 */}
              <button
                onClick={() => fetchNotifications(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                처음
              </button>
              
              {/* 이전 페이지 */}
              <button
                onClick={() => fetchNotifications(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              
              {/* 페이지 번호들 */}
              {(() => {
                const pageNumbers = [];
                const maxButtons = 10;
                let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                
                if (endPage - startPage < maxButtons - 1) {
                  startPage = Math.max(1, endPage - maxButtons + 1);
                }
                
                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(i);
                }
                
                return pageNumbers.map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => fetchNotifications(pageNum)}
                    className={`px-3 py-2 text-sm rounded-lg ${
                      currentPage === pageNum
                        ? 'bg-indigo-600 text-white font-bold'
                        : 'bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                ));
              })()}
              
              {/* 다음 페이지 */}
              <button
                onClick={() => fetchNotifications(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
              
              {/* 마지막 페이지로 */}
              <button
                onClick={() => fetchNotifications(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                마지막
              </button>
            </div>
            
            {/* 페이지당 표시 개수 변경 */}
            <div className="flex items-center space-x-2 text-sm">
              <span>페이지당</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  fetchNotifications(1);
                }}
                className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="20">20개</option>
                <option value="50">50개</option>
                <option value="100">100개</option>
                <option value="200">200개</option>
              </select>
              <span>표시</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationManagement;